import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ErrorMessageComponent } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  FlightStatusForm,
  FlightStatusFormData
} from '../models/interfaces/flight-status-form.interface';
import { FlightStatusHelperComponent } from './flight-status-helper.component';
import { FlightStatusFormHandler } from './handlers/flight-status-form.handler';
import { SelectedAirport } from './models/validation.interfaces';
import { FlightStatusAirportService } from './services/flight-status-airport.service';
import { FlightStatusValidationService } from './services/flight-status-validation.service';
import { FlightStatusService } from './services/flight-status.service';
@Component({
  selector: 'idp-flight-status',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ErrorMessageComponent],
  providers: [
    FlightStatusService,
    FlightStatusAirportService,
    FlightStatusFormHandler,
    FlightStatusValidationService
  ],
  templateUrl: './flight-status.component.html',
  styleUrls: ['./flight-status.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FlightStatusComponent
  extends FlightStatusHelperComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() override content = {} as FlightStatusForm;
  @Input() selectedAirport: SelectedAirport = {};
  @Input() destinationType = '';
  @Input() predictiveCities: { id: string; label: string }[] = [];
  @Input() isLoading = false;
  @Input() isSelected = false;
  @Output() openModalEmit = new EventEmitter<{
    isOpen: boolean;
    triggerType?: 'origin' | 'destination';
  }>();

  @Output() retrieveFlightStatus = new EventEmitter<FlightStatusFormData>();
  @Output() analyticsEvent = new EventEmitter<HTMLElement>();
  @Output() airportSwapped = new EventEmitter<void>();
  override formSubmitted = false;
  isFlightNumberFocused = false;
  isDepartureDateFocused = false;

  ngOnInit(): void {
    this.flightStatusForm = this.formHandler.createForm();
    this.setSelectedDateText(this.formHandler.getInitialDateText());
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.clearError(changes);
  }

  ngAfterViewInit(): void {
    this.formHandler.initializeAirportCode(() => {
      const triggerType = this.airportService.getLastClickedTrigger();
      if (triggerType) {
        this.openModalEmit.emit({ isOpen: true, triggerType });
      }
    }, this.content);
    this.setupReactiveValidation();
    this.setupAirportSwapHandler(this.airportSwapped);
  }

  ngOnDestroy(): void {
    this.formHandler.destroy();
  }

  triggerFlightStatus(): void {
    this.formSubmitted = true;
    this.clearErrors();
    this.validateForm();
    if (this.errorCount() === 0) {
      const formData = this.emitFormData();
      this.retrieveFlightStatus.emit(formData);
    }
  }

  focusFirstErrorField(): void {
    const firstErrorKey = Object.keys(this.errors).find((key) => {
      const msg = this.errors[key];
      return typeof msg === 'string' && msg.trim().length > 0;
    });

    if (!firstErrorKey) return;
    const selectorByField: Record<string, string> = {
      departureDate: 'button[aria-label="Select departure date"]',
      flightNumber: '#flightNumber',
      origin: '#flight-status-airport-code button:first-of-type',
      destination: '#flight-status-airport-code button:last-of-type',
      route: '#flight-status-airport-code button:first-of-type'
    };
    this.openModalIfAirportError(firstErrorKey);
    const selector = selectorByField[firstErrorKey];
    if (!selector) return;
    const el = document.querySelector<HTMLElement>(selector);
    el?.focus();
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  onFlightNumberInput(event: Event): void {
    this.formHandler.processFlightNumberInput(event, this.flightStatusForm);
    if (this.formSubmitted) {
      this.clearErrors();
      this.validateForm();
    }
    if (this.formSubmitted) {
      const v = this.flightStatusForm.get('flightNumber')?.value?.trim();
      if (v) {
        ['origin', 'destination', 'route'].forEach(
          (k) => (this.errors[k] = '')
        );
      }
    }
  }

  openModalIfAirportError(errorKey: string): void {
    if (errorKey === 'origin' || errorKey === 'destination') {
      this.openModalEmit.emit({
        isOpen: true,
        triggerType: errorKey
      });
    }
  }
}
